import { useState, useEffect, useMemo } from 'react'
import type { ParsedFile } from './Step2_UploadFile'
import type { ImportTarget } from './Step1_SelectTarget'

interface Step3Props {
  parsedFile: ParsedFile
  target: string
  targets: ImportTarget[]
  onMapped: (mappings: Record<string, string>) => void
  onBack: () => void
}

const SKIP = '__skip__'

function Step3_ColumnMapping({ parsedFile, target, targets, onMapped, onBack }: Step3Props) {
  const targetConfig = useMemo(
    () => targets.find((t) => t.key === target),
    [targets, target]
  )

  const requiredFields = useMemo(() => targetConfig?.required_fields ?? [], [targetConfig])
  const optionalFields = useMemo(() => targetConfig?.optional_fields ?? [], [targetConfig])
  const defaults = useMemo(() => targetConfig?.defaults ?? {}, [targetConfig])
  const allFields = useMemo(() => [...requiredFields, ...optionalFields], [requiredFields, optionalFields])

  const [mappings, setMappings] = useState<Record<string, string>>({})

  // Auto-match on mount
  useEffect(() => {
    const auto: Record<string, string> = {}
    const usedFields = new Set<string>()

    for (const header of parsedFile.headers) {
      const normalized = header.toLowerCase().replace(/\s+/g, '_')
      const match = allFields.find((f) => {
        if (usedFields.has(f)) return false
        const fNorm = f.toLowerCase()
        return fNorm === normalized || fNorm === header.toLowerCase()
      })
      if (match) {
        auto[header] = match
        usedFields.add(match)
      } else {
        auto[header] = SKIP
      }
    }
    setMappings(auto)
  }, [parsedFile.headers, allFields])

  const updateMapping = (header: string, field: string) => {
    setMappings((prev) => ({ ...prev, [header]: field }))
  }

  // Check for unmapped required fields (that have no defaults)
  const mappedFields = new Set(Object.values(mappings).filter((v) => v !== SKIP))
  const unmappedRequired = requiredFields.filter(
    (f) => !mappedFields.has(f) && !(f in defaults)
  )

  const canProceed = unmappedRequired.length === 0

  const handleNext = () => {
    // Build final mappings, excluding skipped columns
    const final: Record<string, string> = {}
    for (const [header, field] of Object.entries(mappings)) {
      if (field !== SKIP) {
        final[header] = field
      }
    }
    onMapped(final)
  }

  return (
    <div>
      <div className="iw-step-title">Map Columns</div>
      <div className="iw-step-subtitle">
        Map each CSV column to a target field.
        {targetConfig && <> Importing into: <strong>{targetConfig.label}</strong></>}
      </div>

      {unmappedRequired.length > 0 && (
        <div className="iw-mapping-warn">
          Required fields not mapped: {unmappedRequired.join(', ')}
        </div>
      )}

      {target === 'sales_transactions' && (
        <div className="iw-mapping-info">
          Rows with the same <strong>order_ref</strong> will be grouped into a single sales order with multiple line items.
        </div>
      )}

      <div className="iw-mapping-table">
        {parsedFile.headers.map((header) => (
          <div className="iw-mapping-row" key={header}>
            <span className="iw-mapping-source">{header}</span>
            <span className="iw-mapping-arrow">&rarr;</span>
            <select
              className="iw-mapping-select"
              value={mappings[header] ?? SKIP}
              onChange={(e) => updateMapping(header, e.target.value)}
            >
              <option value={SKIP}>Skip this column</option>
              {requiredFields.length > 0 && (
                <optgroup label="Required">
                  {requiredFields.map((f) => (
                    <option key={f} value={f}>
                      {f} *
                    </option>
                  ))}
                </optgroup>
              )}
              {optionalFields.length > 0 && (
                <optgroup label="Optional">
                  {optionalFields.map((f) => (
                    <option key={f} value={f}>
                      {f}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
          </div>
        ))}
      </div>

      <div className="iw-btn-row">
        <button className="iw-btn iw-btn--secondary" onClick={onBack}>
          Back
        </button>
        <button
          className="iw-btn iw-btn--primary"
          disabled={!canProceed}
          onClick={handleNext}
        >
          Next
        </button>
      </div>
    </div>
  )
}

export default Step3_ColumnMapping
